import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { type TableSort } from "@/lib/table"
import { cn } from "@/lib/utils"

export interface SortOption {
  /** Matches the column id / TableSort.field. */
  id: string
  label: string
  /** Direction labels, e.g. "Newest first" / "Oldest first". */
  ascLabel?: string
  descLabel?: string
}

export interface SortButtonProps {
  options: SortOption[]
  value: TableSort | null
  onChange: (v: TableSort | null) => void
  /** Trigger label. Defaults to "Sort". */
  label?: string
  className?: string
}

export function SortButton({
  options,
  value,
  onChange,
  label = "Sort",
  className,
}: SortButtonProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const active = value ? options.find((o) => o.id === value.field) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="sm"
          leadingIcon={
            value?.dir === "asc" ? <ArrowUp /> : value?.dir === "desc" ? <ArrowDown /> : <ArrowUpDown />
          }
          aria-label={
            active
              ? `${label}: ${active.label}, ${value?.dir === "asc" ? "ascending" : "descending"}`
              : label
          }
          className={className}
        >
          {active ? active.label : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[260px] p-0">
        <SortPanel
          options={options}
          value={value}
          onChange={onChange}
          onClose={() => {
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export interface SortPanelProps {
  options: SortOption[]
  value: TableSort | null
  onChange: (v: TableSort | null) => void
  onClose: () => void
}

export function SortPanel({ options, value, onChange, onClose }: SortPanelProps): JSX.Element {
  const currentField = value?.field ?? null
  const currentDir = value?.dir ?? "desc"

  const selectField = (id: string) => {
    onChange({ field: id, dir: currentDir })
  }

  const selectDir = (dir: "asc" | "desc") => {
    if (!currentField) return
    onChange({ field: currentField, dir })
  }

  const clear = () => {
    onChange(null)
    onClose()
  }

  const activeOption = options.find((o) => o.id === currentField)

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-3.5 py-2.5">
        <div className="font-id text-eyebrow uppercase tracking-[0.14em] text-text-3">Sort</div>
        {value && (
          <button
            type="button"
            onClick={clear}
            className="text-caption text-text-2 underline underline-offset-2 hover:text-text"
          >
            Clear
          </button>
        )}
      </div>

      <div className="px-3.5 py-3">
        <div
          className="mb-1.5 font-id text-micro uppercase tracking-[0.12em] text-text-3"
          id="sort-field-label"
        >
          Sort by
        </div>
        <div role="radiogroup" aria-labelledby="sort-field-label" className="space-y-0.5">
          {options.map((opt) => {
            const selected = currentField === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  selectField(opt.id)
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
                  "text-body-sm text-text transition-colors duration-120 ease-out",
                  "hover:bg-surface-sunken",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
                  selected && "bg-brand-tint/60",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "grid size-3.5 shrink-0 place-items-center rounded-full border",
                    selected ? "border-brand" : "border-border-strong",
                  )}
                >
                  {selected && <span className="size-1.5 rounded-full bg-brand" />}
                </span>
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
              </button>
            )
          })}
        </div>

        <div
          className="mt-3.5 mb-1.5 font-id text-micro uppercase tracking-[0.12em] text-text-3"
          id="sort-dir-label"
        >
          Direction
        </div>
        <div
          role="radiogroup"
          aria-labelledby="sort-dir-label"
          className="grid grid-cols-2 gap-1 rounded-md bg-surface-sunken p-0.5"
        >
          <DirTab
            active={currentDir === "asc"}
            disabled={!currentField}
            onClick={() => {
              selectDir("asc")
            }}
            icon={<ArrowUp className="size-3.5" aria-hidden="true" />}
            label={activeOption?.ascLabel ?? "Ascending"}
          />
          <DirTab
            active={currentDir === "desc"}
            disabled={!currentField}
            onClick={() => {
              selectDir("desc")
            }}
            icon={<ArrowDown className="size-3.5" aria-hidden="true" />}
            label={activeOption?.descLabel ?? "Descending"}
          />
        </div>
      </div>
    </div>
  )
}

function DirTab({
  active,
  disabled,
  onClick,
  icon,
  label,
}: {
  active: boolean
  disabled: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5",
        "text-caption transition-colors duration-120 ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        "disabled:cursor-not-allowed disabled:opacity-45",
        active ? "bg-surface text-text shadow-e1" : "text-text-3 hover:text-text",
      )}
    >
      {icon}
      {label}
    </button>
  )
}
